import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faSpinner } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import StockExchangeSourceCodeFields from '@/components/admin/stock-exchanges/StockExchangeSourceCodeFields';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showErrorToast } from '@/utils/toasts';
import { secondsToTimeString, timeStringToSeconds } from '@/utils/helpers';
import type { StockExchange } from '@/models';

interface EditStockExchangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  stockExchange: StockExchange;
  onStockExchangeUpdated: (stockExchange: StockExchange) => void;
}

export default function EditStockExchangeModal({ isOpen, onClose, stockExchange, onStockExchangeUpdated }: EditStockExchangeModalProps) {
  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [openingTime, setOpeningTime] = useState('09:30');
  const [closingTime, setClosingTime] = useState('16:00');
  const [googleSheetsCode, setGoogleSheetsCode] = useState('');
  const [eodhdCode, setEodhdCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Initialize form values when modal opens or stockExchange changes
  useEffect(() => {
    if (isOpen && stockExchange !== null) {
      setIdentifier(stockExchange.identifier);
      setName(stockExchange.name);
      setTimezone(stockExchange.timezone);
      setOpeningTime(secondsToTimeString(stockExchange.openingTime));
      setClosingTime(secondsToTimeString(stockExchange.closingTime));
      setGoogleSheetsCode(stockExchange.googleSheetsCode ?? '');
      setEodhdCode(stockExchange.eodhdCode ?? '');
    }
  }, [isOpen, stockExchange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!identifier.trim()) {
      showErrorToast('Bitte geben Sie einen Identifier ein');
      return;
    }

    if (!name.trim()) {
      showErrorToast('Bitte geben Sie einen Namen ein');
      return;
    }

    setLoading(true);

    try {
      const updatedStockExchange = await APIRoutes.updateStockExchange(stockExchange.id, {
        identifier: identifier.trim().toUpperCase(),
        name: name.trim(),
        timezone: timezone,
        openingTime: timeStringToSeconds(openingTime),
        closingTime: timeStringToSeconds(closingTime),
        googleSheetsCode: googleSheetsCode !== '' ? googleSheetsCode : null,
        eodhdCode: eodhdCode !== '' ? eodhdCode : null,
      });
      onStockExchangeUpdated(updatedStockExchange);
      onClose();
    } catch (err) {
      handleAPIError(err, 'Fehler beim Aktualisieren der Börse');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Börse bearbeiten"
      icon={<FontAwesomeIcon icon={faGlobe} className="w-5 h-5 text-cyan-600" />}
      maxWidth="md"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="mb-6">
          <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Identifier
          </label>
          <input
            type="text"
            id="identifier"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value.toUpperCase())}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
            placeholder="z.B. NYSE, NASDAQ, FWB, XETRA"
            disabled={loading}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Eindeutiger Kurzbezeichner der Börse (z.B. NYSE, NASDAQ, FWB)
          </p>
        </div>

        <div className="mb-6">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
            placeholder="z.B. New York Stock Exchange"
            disabled={loading}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Vollständiger Name der Börse
          </p>
        </div>

        <div className="mb-6">
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Zeitzone
          </label>
          <input
            type="text"
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
            placeholder="z.B. America/New_York, Europe/Berlin"
            disabled={loading}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            IANA Zeitzone des Börsenstandorts (z.B. America/New_York, Europe/Berlin)
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label htmlFor="openingTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Öffnungszeit
            </label>
            <input
              type="time"
              id="openingTime"
              value={openingTime}
              onChange={(e) => setOpeningTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="closingTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Schlusszeit
            </label>
            <input
              type="time"
              id="closingTime"
              value={closingTime}
              onChange={(e) => setClosingTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
              disabled={loading}
            />
          </div>
        </div>

        <StockExchangeSourceCodeFields
          googleSheetsCode={googleSheetsCode}
          eodhdCode={eodhdCode}
          onGoogleSheetsCodeChange={setGoogleSheetsCode}
          onEodhdCodeChange={setEodhdCode}
          disabled={loading}
        />

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !identifier.trim() || !name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Speichere...
              </>
            ) : (
              'Speichern'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
